import React, {useEffect, useRef, useState} from 'react';
import ReviewFilters from './ReviewFilters.jsx';
import Review from './Review.jsx';
import AddReviewButton from './AddReviewButton.jsx';
import createReviewFiltersQueryString from '../../helper/create-review-filters-query-string';
import Url from '@core/scripts/helper/url';
import fetchWithCSRF from '@core/scripts/helper/fetch-with-csrf';
import Logger from '@core/scripts/helper/logger';
import {Button, Typography} from '@bonprix/design-system';
import Landmark from '@core/scripts/components/landmark-adapter';
import ProductAvailability from "../ProductAvailability.jsx";

let activeFilters = [];
let reviewPage = 1;

export default function Reviews({reviewModel, isLoggedIn, loginLink, currentSelectedItem, fullWidth}) {
    const {reviewContent, texts, showMoreReviews} = reviewModel;
    const {count, filters, reviews, stars, averageRating} = reviewContent;

    const [displayReviews, setDisplayReviews] = useState(reviews);
    const [currentCount, setCurrentCount] = useState(count);
    const [sorting, setSorting] = useState({type: 'NEWEST', text: texts?.ada12_review_filter_by_date});
    const [showMoreReviewsButton, setShowMoreReviewsButton] = useState(showMoreReviews);
    const [loading, setLoading] = useState(false);

    const firstUpdate = useRef(true);

    const onFilter = (filter) => {
        setFilters(filter);
        reviewPage = 1;
        getReviews();
    };

    const onRemoveFilter = (currentFilter) => {
        activeFilters = activeFilters.filter((filter) => filter.type !== currentFilter.type);
        activeFilters = activeFilters.filter((filter) => {
            return filter.type !== currentFilter.type;
        });
        getReviews();
    };

    const onRemoveAllFilters = () => {
        activeFilters = [];
        getReviews();
    };

    const onSort = (sorting) => setSorting(sorting);

    useEffect(() => {
        if (firstUpdate.current) {
            firstUpdate.current = false;
            return;
        }
        getReviews(false);
    }, [sorting]);

    const setFilters = (newFilter) => {
        // is new filter type already active?
        if (activeFilters.find((filter) => filter.type === newFilter.type)) {
            activeFilters.forEach((filter) => {
                if (filter.type === newFilter.type) {
                    filter.value = newFilter.value;
                }
            });
        } else {
            activeFilters.push(newFilter);
        }
    };

    const getReviews = (append = false) => {
        const filtersQueryString = createReviewFiltersQueryString(activeFilters);
        const sortingQueryString = sorting?.type ? `&sortingType=${sorting?.type}` : '';
        const url = Url.toAbsolute(
            `/ajax/reviews/?styleId=${reviewModel.styleId}&page=${reviewPage}${
                activeFilters.length > 0 ? filtersQueryString : ''
            }${sortingQueryString}`
        );

        fetchWithCSRF(url)
            .then((response) => {
                if (response.ok) {
                    return response.json();
                }
                throw new Error('response was not okay');
            })
            .then((data) => {
                const {reviews, reviewsCount} = data;
                    if (append) {
                        const newDisplayReviews = displayReviews.concat(reviews);
                        setDisplayReviews(newDisplayReviews);
                        setShowMoreReviewsButton(newDisplayReviews.length < reviewsCount);
                    } else {
                        setDisplayReviews(reviews);
                    }
                    setCurrentCount(reviewsCount);
            })
            .catch((e) => {
                Logger.debug({message: 'Could not fetch reviews', error: e});
            })
            .finally(()=>{
                setLoading(false);
            })
        ;
    };

    const onShowMoreButtonClick = () => {
        setLoading(true);
        Landmark.sendClickLandmark('adp_reviews_more');
        reviewPage = reviewPage + 1;
        getReviews(true);
    };

    const showFilters = count > 0;

    return (
        <div className="reviews">
            {showFilters && (
                <div className={fullWidth ? "reviews__filters-wrapper__full_width" : "reviews__filters-wrapper"}>
                    <ReviewFilters
                        texts={texts}
                        filters={filters}
                        count={count}
                        currentCount={currentCount}
                        sorting={sorting}
                        stars={stars}
                        averageRating={averageRating}
                        onFilter={onFilter}
                        onRemoveFilter={onRemoveFilter}
                        onRemoveAllFilters={onRemoveAllFilters}
                        onSort={onSort}
                        currentSelectedItem={currentSelectedItem}
                    />
                </div>
            )}
            {displayReviews.length > 0 ? (
                <>
                    <div className={fullWidth ? "reviews__list__full_width" : "reviews__list"}>
                        {displayReviews.map((review) => {
                            return (
                                <Review review={review} texts={texts} isLoggedIn={isLoggedIn} loginLink={loginLink}
                                        key={review.id}/>
                            );
                        })}
                        {showMoreReviewsButton && (
                            <Button
                                loading={loading}
                                onClick={onShowMoreButtonClick}
                                variant="secondary">{texts.mobile_article_rating_showmore}</Button>
                        )}
                        <div className="reviews__add-review">
                            <AddReviewButton texts={texts}/>
                        </div>
                    </div>
                </>
            ) : (
                <div className={'reviews__list' + (!showFilters ? ' -empty' : '') + (fullWidth ? ' l:w-full l:ml-0' : '')}>
                    <div className="reviews__empty-message">
                        <Typography variant="body1" align="center">{texts.bff_reviews_no_reviews}</Typography>
                    </div>
                    <div className="reviews__add-review">
                        <AddReviewButton texts={texts}/>
                    </div>
                </div>
            )}
        </div>
    );
}
